import * as React from "react";
import Image from "next/image";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import Dialog from "src/components/Dialog";
// import Icon from "../Icon";
import Link from "next/link";
import Button from "@src/components/Button";
import { GetAlbumDetailsQuery } from "@src/@types/api.d";

type Props = {
  eventId: number;
  albumDetailsData: GetAlbumDetailsQuery;
  eventSlug: string;
};

export function insertAt(
  array: unknown[],
  index: number,
  ...elementsArray: unknown[]
) {
  array.splice(index, 0, ...elementsArray);
}

const Masonry: React.FC<Props> = ({ eventId, eventSlug, albumDetailsData }) => {
  const mediaData =
    albumDetailsData?.getMedia?.filter((media) =>
      media?.type?.toLowerCase().includes("image")
    ) || [];

  return mediaData.length > 0 ? (
    <div className="bg-[#E2D4D2] lg:pt-[8rem] lg:pb-[6rem] pt-[5rem] pb-[3rem] px-4">
      {/* Grid layout for screens smaller than lg */}
      <div className="block lg:hidden">
        <div
          className={`grid max-w-full grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4 md:gap-6 mb-12`}
        >
          {mediaData.slice(0, 4)?.map((image, index) => {
            if (image && image.meta?.s3_url) {
              return (
                <div
                  key={index}
                  className={`relative aspect-[0.865] cursor-pointer rounded-2xl transition duration-500 hover:scale-[1.02]`}
                >
                  <Image
                    placeholder="blur"
                    blurDataURL={`data:image/svg+xml;base64,${toBase64(
                      shimmer(700, 475)
                    )}`}
                    src={image.meta.s3_url}
                    layout="fill"
                    alt="gallery"
                    objectFit="cover"
                    className="rounded-2xl object-top"
                  />
                </div>
              );
            }
          })}
        </div>
      </div>

      {/* Horizontal scrolling layout for lg screens and above */}
      <div className="hidden lg:block mb-12">
        <div className="relative overflow-hidden">
          <div
            className="flex gap-6 animate-scroll"
            style={{
              width: `${
                mediaData.length * 320 + (mediaData.length - 1) * 24
              }px`, // 320px width + 24px gap
            }}
          >
            {/* First set of images */}
            {mediaData.map((image, index) => {
              if (image && image.meta?.s3_url) {
                return (
                  <div
                    key={`first-${index}`}
                    className="relative w-80 aspect-[0.865] cursor-pointer rounded-2xl transition duration-500 hover:scale-[1.02] flex-shrink-0"
                  >
                    <Image
                      placeholder="blur"
                      blurDataURL={`data:image/svg+xml;base64,${toBase64(
                        shimmer(700, 475)
                      )}`}
                      src={image.meta.s3_url}
                      layout="fill"
                      alt="gallery"
                      objectFit="cover"
                      className="rounded-2xl object-top"
                    />
                  </div>
                );
              }
            })}
            {/* Duplicate set for seamless loop */}
            {mediaData.map((image, index) => {
              if (image && image.meta?.s3_url) {
                return (
                  <div
                    key={`second-${index}`}
                    className="relative w-80 aspect-[0.865] cursor-pointer rounded-2xl transition duration-500 hover:scale-[1.02] flex-shrink-0"
                  >
                    <Image
                      placeholder="blur"
                      blurDataURL={`data:image/svg+xml;base64,${toBase64(
                        shimmer(700, 475)
                      )}`}
                      src={image.meta.s3_url}
                      layout="fill"
                      alt="gallery"
                      objectFit="cover"
                      className="rounded-2xl object-top"
                    />
                  </div>
                );
              }
            })}
          </div>
        </div>
      </div>

      <div
        className={`flex flex-col md:flex-row gap-8 justify-center items-center`}
      >
        <Link
          href={`https://${eventSlug}.${process.env.NEXT_PUBLIC_INAWO_ALBUM_SITE_END}`}
          passHref
        >
          <a target="_blank">
            <Button
              design="transparent"
              type="button"
              label="Save_Date"
              className="!border-[#2C1D1D] !p-0"
            >
              <span className="flex items-center justify-center h-[2.5rem] w-[12rem] !font-eavesSmallCaps text-base !text-[#2C1D1D] border border-[#2C1D1D] rounded hover:scale-105 transition-transform duration-200">
                See full photobook
              </span>
            </Button>
          </a>
        </Link>
      </div>

      <style>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-${
              mediaData.length * 320 + (mediaData.length - 1) * 24
            }px);
          }
        }
        
        .animate-scroll {
          animation: scroll ${mediaData.length * 4}s linear infinite;
        }
        
        .animate-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  ) : (
    <></>
  );
};

export default Masonry;
